import { z } from "zod";
import { localDateTimeSchema, visitServiceSchema } from "./booking";
import { normalizePhone } from "./clients";
import type { FormsPacket } from "./forms";
import type { BillView, PaymentSession } from "./payments";

/** A clinic iPad, as staff see it (Clinic settings → Devices, and "Send forms to…"). */
export interface DeviceView {
  id: string;
  name: string;
  paired: boolean;
  /** A pairing code still waiting to be typed on the iPad, and when it runs out. */
  pairCode: string | null;
  pairExpiresAt: string | null;
  lastSeenAt: string | null;
  /** Seen in the last minute (it checks in every couple of seconds while open). */
  online: boolean;
  /** Showing someone's forms right now. */
  busyWith: { appointmentId: string; clientName: string; sentAt: string | null } | null;
  /** A checkout sent to it, waiting for the client. */
  checkoutFor: { appointmentId: string; sentAt: string } | null;
  /** Where it's used: a clinic location's check-in (null: any of the clinic's), or an event (set on the day). */
  locationId: string | null;
  eventOccurrenceId: string | null;
  placeLabel: string;
  /** Clients can book themselves in as a walk-in on it. */
  allowWalkIns: boolean;
  /** Clients can start the telehealth screening on it (off: it's sent to their phone). */
  allowScreenings: boolean;
  /** The lobby display (Check in, Walk in, Book, a screen saver). */
  lobby: boolean;
}

/** Where an iPad can be used: the clinic's locations, and events on now or soon. */
export interface DevicePlaces {
  locations: { id: string; name: string }[];
  events: { id: string; label: string }[];
}

export const devicePlaceSchema = z.object({ locationId: z.string().uuid().nullable(), eventOccurrenceId: z.string().uuid().nullable() });
export const deviceWalkInsSchema = z.object({ allowWalkIns: z.boolean() });
export const deviceLobbySchema = z.object({ lobby: z.boolean() });
export const deviceScreeningsSchema = z.object({ allowScreenings: z.boolean() });

/** What a paired iPad shows. */
export interface DeviceState {
  name: string;
  clinicName: string;
  /** Where it's used, for the welcome screen ("Check in for your appointment at …"). */
  /** eventSlug: an event's iPad shows the event's look (from its page, /api/e/<slug>). */
  place: { kind: "clinic" | "event"; label: string; eventSlug: string | null };
  job: { appointmentId: string; sentAt: string | null; packet: FormsPacket } | null;
  /** A client's self check-in in progress: each person checked in, and what's left for them. */
  checkIn: { people: DeviceCheckInPerson[] } | null;
  /** The front desk sent a checkout (read it from /api/device/checkout). */
  checkout: boolean;
  /** Clients can book themselves in as a walk-in here. */
  walkIns: boolean;
  /** An event's iPad: someone booked for today's date hasn't checked in yet (false: the welcome goes straight to walk-up). */
  awaitingCheckIn: boolean;
  /** Clients can do the telehealth screening here (false: "send it to my phone" instead). */
  screenings: boolean;
  /** A walk-in waiting on a card on file (null when there's none, or once it's checked in). */
  walkIn: DeviceWalkInView | null;
  /** The lobby display: Book is on its welcome, it has a screen saver, and it wipes after a minute untouched. */
  lobby: boolean;
  /** The lobby's screen saver video (a link that stays the same until the video changes) and its active specials, each opening a page (null: nothing to open). */
  lobbyMedia: {
    videoUrl: string | null;
    specials: { id: string; ribbon: string; headline: string; text: string; path: string | null }[];
    /** The animated menu (null: off). `art`: a link per uploaded slot. */
    signage: { featured: { serviceId: string; art: string | null }[]; hidden: string[]; boardArt: Record<string, string | null>; art: Partial<Record<string, string>> } | null;
  } | null;
}

/** What a walk-in can pick on the iPad: the services bookable online (an event's menu at an event). */
export interface DeviceWalkInMenu {
  /** role: a drip (add-ons go with it), or an event's recovery/extra option (with or without a drip, no add-ons). */
  services: { id: string; name: string; description: string; priceCents: number; addOnIds: string[]; role: "drip" | "extra" }[];
  /** `service`: an event's service offered as an add-on (a shot), booked as its own service beside the drip. */
  addOns: { id: string; name: string; priceCents: number; service: boolean }[];
  /** The heading for the recovery/extra options. */
  extrasTitle: string;
  /** The card and no-show policy they agree to before booking (null when no card is kept on file). */
  agreement: string | null;
}

/** The first time a walk-in can start today, and what it costs. */
export interface DeviceWalkInQuote {
  /** Clinic-local "YYYY-MM-DDTHH:MM" (sent back when booking). */
  start: string;
  /** "2:30 PM" */
  time: string;
  totalCents: number;
}

/** A walk-in held while they save a card on file. */
export interface DeviceWalkInView {
  firstName: string;
  time: string;
  serviceName: string;
  /** The card and no-show policy they agree to. */
  agreement: string;
  /** Their card already on file (all four details matched), if any. */
  onFile: { brand: string; last4: string } | null;
  /** The card reader they can tap or insert on, if the iPad has one. */
  reader: { label: string } | null;
  /** Waiting for a card on the reader. */
  waitingOnReader: boolean;
  /** Why the reader couldn't save the card (try again, or another way). */
  readerError: string | null;
}

const walkInServices = z.array(visitServiceSchema).min(1, "Pick a service").max(8);
export const deviceWalkInQuoteSchema = z.object({ services: walkInServices });
const filled = (label: string) => z.string().trim().min(1, `Enter your ${label}`);
export const deviceWalkInSchema = z.object({
  services: walkInServices,
  start: localDateTimeSchema,
  /** Someone we know: found by last name and date of birth at check-in. */
  returning: z.object({ clientId: z.string().uuid(), lastName: z.string().trim().min(1).max(80), dob: z.string().regex(/^\d{4}-\d{2}-\d{2}$/) }).nullable().default(null),
  /** Someone new: their details. */
  person: z.object({
    firstName: filled("first name").pipe(z.string().max(80)),
    lastName: filled("last name").pipe(z.string().max(80)),
    dob: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Enter your date of birth"),
    mobile: filled("mobile number")
      .pipe(z.string().max(30))
      .transform((v) => normalizePhone(v))
      .pipe(z.string({ message: "Check your mobile number" })),
    email: filled("email").pipe(z.string().toLowerCase().max(254).email("Check your email address")),
  }).nullable().default(null),
  /** They agreed to the card and no-show policy (needed when the clinic keeps a card on file). */
  agreed: z.boolean().default(false),
});
export const deviceWalkInCardSchema = z.object({ method: z.enum(["on_file", "reader", "form"]) });

/** A checkout on the iPad: the client's bills, a tip, and how they pay. */
export interface DeviceCheckoutView {
  clinicName: string;
  bills: Omit<BillView, "nurseId">[];
  tipPercents: number[];
  /** The card on file it can be paid with, if any. */
  card: { brand: string; last4: string } | null;
  /** The card reader to tap or insert on, if there is one. */
  reader: { label: string } | null;
  /** What's still owed on these bills, tips included. */
  dueCents: number;
  /** A payment underway (on the reader, or Stripe's card page). */
  waiting: boolean;
  paid: boolean;
}

export const devicePaySchema = z.object({ method: z.enum(["card_on_file", "card_present", "card"]), /** Card reader: keep the card on file. */ saveCard: z.boolean().default(true) });
export type DevicePayResult = DeviceCheckoutView & { session: PaymentSession | null; message: string | null };
export const sendCheckoutSchema = z.object({ deviceId: z.string().uuid(), billIds: z.array(z.string().uuid()).min(1).max(20), readerId: z.string().uuid().nullable().default(null) });

export interface DeviceCheckInPerson {
  appointmentId: string;
  firstName: string;
  /** "2:00 PM" */
  time: string;
  serviceName: string;
  /** Forms still to sign, and whether the health screening (good faith exam) is waiting. */
  formsNeeded: number;
  screening: boolean;
  /** The screening's link has been texted or emailed to them. */
  screeningSent: boolean;
}

/** Today's visits found for a name and date of birth (theirs first, then the rest of their party). First names only. */
export interface DeviceFound {
  people: { appointmentId: string; firstName: string; time: string; serviceName: string; checkedIn: boolean; self: boolean }[];
  /** Clients with that last name and date of birth (no visit today): they can walk in as themselves. First names only. */
  known: { clientId: string; firstName: string }[];
}

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Enter the date of birth");
export const deviceFindSchema = z.object({ lastName: z.string().trim().min(1, "Enter your last name").max(80), dob: isoDate });
export const deviceCheckInSchema = deviceFindSchema.extend({ appointmentIds: z.array(z.string().uuid()).min(1).max(12) });

export const deviceInputSchema = z.object({ name: z.string().trim().min(1).max(40) });
